import type { SvgIconComponent } from "@mui/icons-material";
import { Button, DialogContent, DialogTitle, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { WorkflowSignalType } from "@entrilia/shared/api/workflowTypes";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import ApproveRequestDialogIcon from "@entrilia/shared/icons/ApproveRequestDialogIcon";
import CancelRequestDialogIcon from "@entrilia/shared/icons/CancelRequestDialogIcon";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import RejectRequestDialogIcon from "@entrilia/shared/icons/RejectRequestDialogIcon";
import RevertDialogIcon from "@entrilia/shared/icons/RevertDialogIcon";
import SubmitRequestDialogIcon from "@entrilia/shared/icons/SubmitRequestDialogIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../api/adminApi";
import { workflowSignalTypeMap } from "../../../utilities/enumCaptions";
import ObjectDuplicatesCheck from "../../entityFields/ObjectDuplicatesCheck";
import useObjectDuplicates from "../../entityFields/useObjectDuplicates";
import useWorkflowActionChecks from "./useWorkflowActionChecks";
import WorkflowActionChecks from "./WorkflowActionChecks";

const executeWorkflowAction = withErrorHandling(adminApi.executeWorkflowAction);

const workflowSignalTypeDialogIconMap: Record<WorkflowSignalType, SvgIconComponent> = {
  Approve: ApproveRequestDialogIcon,
  Reject: RejectRequestDialogIcon,
  Cancel: CancelRequestDialogIcon,
  CreateInvoiceTransaction: SubmitRequestDialogIcon,
  SubmitInvoiceWithoutConfiguredApproval: SubmitRequestDialogIcon,
  SubmitInvoiceForApproval: SubmitRequestDialogIcon,
  DeleteInvoice: DeleteDialogIcon,
  ReturnInvoiceToDraft: RevertDialogIcon,
  BusinessCentralTransactionPosted: SubmitRequestDialogIcon,
};

const getDialogPrompt = (
  signal: WorkflowSignalType,
  objectTypeName: string,
  objectName: string | undefined
): string => {
  switch (signal) {
    case "Approve":
      return objectName ? `Approve ${objectTypeName} - ${objectName}?` : `Approve ${objectTypeName}?`;
    case "Reject":
      return objectName ? `Reject ${objectTypeName} - ${objectName}?` : `Reject ${objectTypeName}?`;
    case "Cancel":
      return objectName ? `Cancel ${objectTypeName} - ${objectName}?` : `Cancel ${objectTypeName}?`;
    case "CreateInvoiceTransaction":
      return "Create transaction?";
    case "SubmitInvoiceWithoutConfiguredApproval":
      return "Create transaction?";
    case "SubmitInvoiceForApproval":
      return "Submit invoice for approval?";
    case "DeleteInvoice":
      return "Delete invoice?";
    case "ReturnInvoiceToDraft":
      return "Return invoice to draft?";
    default:
      return `${workflowSignalTypeMap.get(signal)?.caption || signal}?`;
  }
};

const getDialogDescription = (signal: WorkflowSignalType, objectTypeName: string): string => {
  switch (signal) {
    case "Approve":
      return `You're about to approve this ${objectTypeName}.`;
    case "Reject":
      return `You're about to reject this ${objectTypeName}. This action is permanent and can't be undone.`;
    case "Cancel":
      return `You're about to cancel this ${objectTypeName}. This action is permanent and can't be undone.`;
    case "CreateInvoiceTransaction":
      return "You're about to create transaction in Fund Operations.";
    case "SubmitInvoiceWithoutConfiguredApproval":
      return "You're about to create transaction in Fund Operations.";
    case "SubmitInvoiceForApproval":
      return "You're about to submit this invoice for approval.";
    case "DeleteInvoice":
      return "You're about to delete this invoice. This action is permanent and can't be undone.";
    case "ReturnInvoiceToDraft":
      return "You're about to return this invoice to draft.";
    default:
      return "";
  }
};

const showCommentInput = (signal: WorkflowSignalType): boolean =>
  signal === "Approve" || signal === "Reject" || signal === "Cancel";

const isCommentRequired = (signal: WorkflowSignalType): boolean => signal === "Reject" || signal === "Cancel";

interface Props {
  workflowId: string;
  signalType: WorkflowSignalType;
  objectTypeName: string;
  objectName?: string;
  selectedChangeSetId?: string;
  expectedActivityId?: string;
  objectType?: string;
  objectId?: string;
  showActionChecks?: boolean;
  onClose: () => void;
  onActionExecuted: (signalType: WorkflowSignalType) => void;
}

const WorkflowActionConfirmationDialog = ({
  workflowId,
  signalType,
  objectTypeName,
  objectName,
  selectedChangeSetId,
  expectedActivityId,
  objectType,
  objectId,
  showActionChecks,
  onClose,
  onActionExecuted,
}: Props) => {
  const { sendNotificationError } = useNotificationContext();

  const [executing, setExecuting] = useState(false);
  const [comment, setComment] = useState("");

  const actionChecks = useWorkflowActionChecks(showActionChecks ? { workflowId, action: signalType } : undefined);
  const duplicates = useObjectDuplicates(
    objectType && objectId ? { objectType, objectId, action: signalType } : undefined
  );

  const handleConfirm = async () => {
    if (isCommentRequired(signalType) && !comment.trim()) {
      return;
    }

    setExecuting(true);

    const [resp, error] = await executeWorkflowAction(workflowId, {
      signalType,
      selectedChangeSetId,
      commentUserInput: comment.trim() || undefined,
      expectedActivityId,
    });

    setExecuting(false);

    if (error) {
      logError(error, "[ObjectWorkflowButtons] executeWorkflowAction");
      sendNotificationError(error.message);
      return;
    }

    if (!resp.isSuccess) {
      const failureMessages = (resp.preconditionFailures ?? []).map((f) => f.message).join("\n");
      sendNotificationError(failureMessages || resp.errorMessage || "Workflow action could not be executed");
      return;
    }

    onActionExecuted(signalType);
    onClose();
  };

  const Icon = workflowSignalTypeDialogIconMap[signalType] ?? ApproveRequestDialogIcon;
  const signalAttributes = workflowSignalTypeMap.get(signalType);

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>Confirm</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <Icon sx={{ width: 80, height: 80 }} />

          <Typography variant="h6" sx={{ textAlign: "center" }}>
            {getDialogPrompt(signalType, objectTypeName, objectName)}
          </Typography>

          <Typography color="textSecondary" sx={{ textAlign: "center" }}>
            {getDialogDescription(signalType, objectTypeName)}
          </Typography>

          <WorkflowActionChecks
            action={signalType}
            blockers={actionChecks.blockers}
            warnings={actionChecks.warnings}
            hasFailed={actionChecks.hasFailed}
          />

          {objectType && <ObjectDuplicatesCheck objectType={objectType} objectDuplicates={duplicates} />}

          {showCommentInput(signalType) && (
            <TextField
              multiline
              rows={4}
              spellCheck
              fullWidth
              placeholder={isCommentRequired(signalType) ? "Add reason..." : "Add comment (optional)..."}
              onChange={(e) => setComment(e.target.value)}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button
          color={signalAttributes?.color === "error" ? "error" : "primary"}
          variant={signalAttributes?.color === "error" ? "outlined" : "contained"}
          loading={executing || actionChecks.isFetching || duplicates.isFetching}
          disabled={actionChecks.blockers.length > 0 || (isCommentRequired(signalType) && !comment.trim())}
          onClick={handleConfirm}
        >
          {signalAttributes?.caption || signalType}
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default WorkflowActionConfirmationDialog;
